import React, { Dispatch, forwardRef, SetStateAction, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { CheckboxChangeEvent, TextAreaChangeEvent, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { debounce } from 'lodash';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import { TextArea } from '@components-clean/core/text-area';
import { TextBox } from '@components-clean/core/text-box';
import {
    AddEditCustomReportErrors,
    AddEditCustomReportState,
    CustomReportPage,
} from '@pages-clean/risk-assessment/reporting/excel-listings/custom-report-helpers';
import { useTranslation } from 'hooks';
import { ReportDefinitionFields } from 'queries/generated/fragments.generated';
import { checkRequiredFields, validateFields } from 'utils/validation';

interface CustomReportStep1NameProps {
    show: boolean;
    reportDefinition: ReportDefinitionFields;
    state: AddEditCustomReportState;
    setState: Dispatch<SetStateAction<AddEditCustomReportState>>;
    updateErrorState: (errorState: AddEditCustomReportErrors, pageNumber: number) => void;
    isNameUnique: () => boolean;
    canViewInternalRisks: boolean;
    updateRiskInternalColumns: (include: boolean) => void;
}

// TODO: refactor to use ref as a prop - forwardRef was deprecated in React 19
// Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
// eslint-disable-next-line @eslint-react/no-forward-ref
const CustomReportStep1Name = forwardRef(
    (
        {
            show,
            reportDefinition,
            state,
            setState,
            updateErrorState,
            isNameUnique,
            canViewInternalRisks,
            updateRiskInternalColumns,
        }: CustomReportStep1NameProps,
        ref,
    ) => {
        const { t } = useTranslation();

        const initialErrors: AddEditCustomReportErrors = {
            name: '',
            description: '',
        };

        const [errors, setErrors] = useState({ ...initialErrors } as AddEditCustomReportErrors);

        const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
            const error = validateFields(value, field, length, required);
            setErrors({
                ...errors,
                ...error,
            });
        };

        const checkNameUnique = () => {
            if (isNameUnique()) return true;

            const uniqueNameError = t('reports:customReportNonUniqueNameError');
            const nameError = errors.name ? `${errors.name} ${uniqueNameError}` : uniqueNameError;

            setErrors({
                ...errors,
                name: nameError,
            });

            return false;
        };

        function validationOnUpdate() {
            const requiredFields = ['name'];

            if (!checkRequiredFields({ fields: requiredFields, state: state.reportDefinition, errors, setErrors }))
                return false;

            return checkNameUnique();
        }

        useImperativeHandle(ref, () => ({
            validationOnUpdate() {
                return validationOnUpdate();
            },
        }));

        // Bubbling every keystroke straight to the parent's setState re-renders the whole dialog tree
        // (and everything behind it) on every character. Under a held-down key that fires fast enough,
        // this cascades into "Maximum update depth exceeded" elsewhere in the tree. The TextBox/TextArea
        // already echo the typed value instantly via their own local state, so debouncing the bubble-up
        // to parent state costs nothing visible while it settles. Built once via useRef so the debounce
        // timer actually coalesces rapid calls instead of a fresh instance winning on every render.
        const debouncedUpdateReportDefinition = useRef(
            debounce((fieldName: string, value: string) => {
                setState((prevState) => ({
                    ...prevState,
                    reportDefinition: {
                        ...prevState.reportDefinition,
                        [fieldName]: value,
                    },
                }));
            }, 300),
        ).current;

        useEffect(() => () => debouncedUpdateReportDefinition.cancel(), [debouncedUpdateReportDefinition]);

        const onChange =
            (fieldName: string, length?: number, required?: boolean) =>
            (event: TextBoxChangeEvent | TextAreaChangeEvent) => {
                const { value } = event.target;
                debouncedUpdateReportDefinition(fieldName, value as string);
                validate(value as string, fieldName, length, required);
            };

        const onInternalRisksCheckboxChange = (event: CheckboxChangeEvent) => {
            const { value } = event;

            setState({
                ...state,
                reportDefinition: {
                    ...reportDefinition,
                    includeInternalRisks: value,
                },
            });

            updateRiskInternalColumns(value);
        };

        const onNotApplicableRisksCheckboxChange = (event: CheckboxChangeEvent) => {
            const { value } = event;

            setState({
                ...state,
                reportDefinition: {
                    ...reportDefinition,
                    includeNotApplicableRisks: value,
                },
            });
        };

        const onDraftRisksCheckboxChange = (event: CheckboxChangeEvent) => {
            const { value } = event;

            setState({
                ...state,
                reportDefinition: {
                    ...reportDefinition,
                    includeDraftRisks: value,
                },
            });
        };

        const onObsoleteRisksCheckboxChange = (event: CheckboxChangeEvent) => {
            const { value } = event;

            setState({
                ...state,
                reportDefinition: {
                    ...reportDefinition,
                    includeObsoleteRisks: value,
                },
            });
        };

        useEffect(() => {
            updateErrorState(errors, CustomReportPage.step1Name);
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/exhaustive-deps
        }, [errors]);

        return (
            <>
                {show && (
                    <div className="step1-name">
                        <div className="k-pb-sm formFieldGap left-part k-pt-xs">
                            <div className="k-d-flex k-w-full k-flex-wrap k-py-sm k-shrink-0 k-pl-md k-gap-y-2 checkboxContainer">
                                {canViewInternalRisks && (
                                    <div className="!k-flex-basis-2/5">
                                        <CoreCheckbox
                                            rounded={null}
                                            id="includeInternalRisksCheckbox"
                                            checked={state.reportDefinition.includeInternalRisks}
                                            onChange={onInternalRisksCheckboxChange}
                                            label={t('reports:includeInternalRisks')}
                                            data-testid="include-internal-risks"
                                            size="medium"
                                        />
                                    </div>
                                )}
                                <div className="!k-flex-basis-2/5">
                                    <CoreCheckbox
                                        rounded={null}
                                        id="includeDraftRisksCheckbox"
                                        checked={state.reportDefinition.includeDraftRisks}
                                        onChange={onDraftRisksCheckboxChange}
                                        label={t('reports:includeDraftRisks')}
                                        data-testid="include-draft-risks"
                                        size="medium"
                                    />
                                </div>
                                <div className="!k-flex-basis-2/5">
                                    <CoreCheckbox
                                        rounded={null}
                                        id="includeObsoleteRisksCheckbox"
                                        checked={state.reportDefinition.includeObsoleteRisks}
                                        onChange={onObsoleteRisksCheckboxChange}
                                        label={t('reports:includeObsoleteRisks')}
                                        data-testid="include-obsolete-risks"
                                        size="medium"
                                    />
                                </div>
                                <div className="!k-flex-basis-2/5">
                                    <CoreCheckbox
                                        rounded={null}
                                        id="includeNotApplicableRisksCheckbox"
                                        checked={state.reportDefinition.includeNotApplicableRisks}
                                        onChange={onNotApplicableRisksCheckboxChange}
                                        label={t('reports:includeNotApplicableRisks')}
                                        data-testid="include-not-applicable-risks"
                                        size="medium"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="type-edc-wrapper mt k-px-3">
                            <TextBox
                                label={t('reports:customReportStep1Name')}
                                id="add-edit-report-config-name"
                                className="add-edit-report-config-name"
                                value={reportDefinition.name ?? undefined}
                                onChange={onChange('name', 100, true)}
                                placeholder={t('reports:customReportStep1NameHint')}
                                color="secondary"
                                maxLength={100}
                                validationMessage={errors.name}
                                valid={!errors.name}
                                data-testid="add-edit-report-config-name"
                            />
                        </div>
                        <div className="type-edc-wrapper mt k-px-3">
                            <TextArea
                                label={t('reports:customReportStep1Description')}
                                id="add-edit-report-config-description"
                                className="add-edit-report-config-description"
                                rows={14}
                                value={reportDefinition.description ?? undefined}
                                onChange={onChange('description', 600, false)}
                                placeholder={t('reports:customReportStep1DescriptionHint')}
                                color="secondary"
                                maxLength={600}
                                validationMessage={errors.description}
                                valid={!errors.description}
                                data-testid="add-edit-report-config-description"
                            />
                        </div>
                    </div>
                )}
            </>
        );
    },
);

export default CustomReportStep1Name;
